import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useHealth, setPremiumHealthBonus } from "@/hooks/useHealth";
import { useXP } from "@/hooks/useXP";
import { useChallenges } from "@/hooks/useChallenges";
import { useMusicSettings } from "@/hooks/useMusicSettings";
import { useShop } from "@/hooks/useShop";
import { ALL_LEVELS as LEVELS, getCompletedLevels, getLevelBestScore, getLevelStars } from "@/config/levels";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { XPBadge } from "@/components/game/XPDisplay";
import { ShopModal, StarBadge, CoinBadge } from "@/components/game/ShopModal";
import { SettingsModal } from "@/components/game/SettingsModal";
import { WorldSelector } from "@/components/game/WorldSelector";
import { DailyChallengesPanel } from "@/components/game/DailyChallenges";
import { SidebarMenu, MenuButton } from "@/components/game/SidebarMenu";
import { DailyBoxReward, useWheelOfFortune } from "@/components/game/WheelOfFortune";
import { BattlePassModal } from "@/components/game/BattlePass";
import { PremiumMembershipModal } from "@/components/game/PremiumMembershipModal";
import { HealthBar, HealthBarMini } from "@/components/game/HealthBar";
import { CosmeticsModal } from "@/components/game/CosmeticsModal";
import { FriendsModal } from "@/components/game/FriendsModal";
import { ChatModal } from "@/components/game/ChatModal";
import { InventoryModal } from "@/components/game/InventoryModal";
import { GrandExchangeModal } from "@/components/game/GrandExchangeModal";
import { ParallaxBackground } from "@/components/game/ParallaxBackground";
import { HamburgerMenu } from "@/components/game/HamburgerMenu";
import { ProfileSidebar } from "@/components/game/ProfileSidebar";
import { TopHUD } from "@/components/game/TopHUD";
import { SocialMenu } from "@/components/game/SocialMenu";
import { TutorialOverlay } from "@/components/game/TutorialOverlay";
import { PvPArenaModal } from "@/components/game/PvPArenaModal";
import { ProfileSwitcherModal } from "@/components/game/ProfileSwitcherModal";
import { DailyRewardModal } from "@/components/game/DailyRewardModal";
import { 
  Play, 
  Lock, 
  Star, 
  Trophy,
  LogOut,
  Heart,
  Clock,
  Crown,
  X,
  ChevronUp,
  ChevronDown,
  Menu,
  MessageCircle,
  Target,
  Zap,
  TrendingUp,
  Award,
  Swords
} from "lucide-react";

// Level tile component for the path
function LevelTile({ level, isUnlocked, isCompleted, canPlay, stars, onClick, isSelected, position, isCurrent, index }) {
  const getDifficultyGradient = (difficulty) => {
    switch (difficulty) {
      case "Easy": return "from-green-400 to-green-600";
      case "Medium": return "from-yellow-400 to-orange-500";
      case "Hard": return "from-orange-500 to-red-500";
      case "Expert": return "from-red-500 to-pink-600";
      case "Master": return "from-purple-500 to-violet-600";
      case "Legendary": return "from-amber-400 to-yellow-500";
      case "Prestige": return "from-violet-500 to-pink-500";
      default: return "from-gray-400 to-gray-600";
    }
  };

  // Floating animation delay based on index
  const floatDelayClass = `level-node-float-delay-${index % 10}`;

  return (
    <button
      onClick={onClick}
      disabled={!isUnlocked || !canPlay}
      data-testid={`level-tile-${level.id}`}
      className={`
        absolute transform -translate-x-1/2 -translate-y-1/2
        w-14 h-14 sm:w-16 sm:h-16
        transition-all duration-300
        level-node-3d level-node-float ${floatDelayClass}
        level-tile-entrance entrance-delay-${index}
        ${isUnlocked && canPlay ? 'level-node-hover cursor-pointer' : 'cursor-not-allowed'}
        ${isSelected ? 'scale-125 z-40' : 'z-10'}
        ${!isUnlocked ? 'level-locked' : ''}
        ${isCurrent ? 'level-current-glow' : ''}
      `}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
      }}
    >
      {/* 3D Platform base - bottom layer */}
      <div className="absolute inset-0 rounded-full transform translate-y-2">
        <div className={`
          w-full h-full rounded-full
          ${isCompleted ? 'bg-amber-800' : isUnlocked ? 'bg-gray-700' : 'bg-gray-800'}
        `} />
      </div>
      
      {/* 3D Platform middle layer */}
      <div className="absolute inset-0 rounded-full transform translate-y-1">
        <div className={`
          w-full h-full rounded-full
          ${isCompleted ? 'bg-amber-700' : isUnlocked ? 'bg-gray-600' : 'bg-gray-700'}
        `} />
      </div>
      
      {/* Main platform surface */}
      <div className={`
        absolute inset-0 rounded-full 
        bg-gradient-to-br ${isUnlocked ? getDifficultyGradient(level.difficulty) : 'from-gray-500 to-gray-700'}
        border-4 ${isCompleted ? 'border-yellow-300 level-completed-glow' : isSelected ? 'border-white ring-4 ring-yellow-400/50' : 'border-white/60'}
        ${isCurrent ? 'level-current-pulse' : ''}
        shadow-xl
        transition-all duration-300
      `}>
        {/* Top shine highlight */}
        <div className="absolute top-1 left-2 right-2 h-1/3 rounded-full bg-gradient-to-b from-white/50 to-transparent" />
        
        {/* Inner shadow for depth */}
        <div className="absolute inset-2 rounded-full bg-gradient-to-b from-transparent to-black/20" />
      </div>

      {/* Level content - number always visible */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-display font-bold text-xl sm:text-2xl drop-shadow-lg ${isUnlocked ? 'text-white' : 'text-white/70'}`}
          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          {level.id}
        </span>
        {isCompleted && stars > 0 && (
          <div className="flex gap-0.5 -mt-1">
            {[1, 2, 3].map((star) => (
              <Star
                key={star}
                className={`w-2.5 h-2.5 sm:w-3 sm:h-3 drop-shadow ${
                  star <= stars 
                    ? 'text-yellow-300 fill-yellow-300' 
                    : 'text-white/30'
                }`}
              />
            ))}
          </div>
        )}
        {!isUnlocked && (
          <Lock className="w-4 h-4 text-white/60 absolute -bottom-0.5 drop-shadow" />
        )}
      </div>

      {/* Target fruit badge - floating */}
      {isUnlocked && (
        <div className="absolute -bottom-1 -right-1 text-base sm:text-lg drop-shadow-lg"
          style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))' }}>
          {level.targetEmoji}
        </div>
      )}

      {/* Completed sparkle effect */}
      {isCompleted && (
        <>
          <div className="absolute inset-0 rounded-full bg-yellow-400/20 animate-pulse" />
          <div className="absolute -top-2 -right-2 text-sm animate-star-twinkle">✨</div>
        </>
      )}
      
      {/* Ground shadow */}
      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-10 h-3 bg-black/30 rounded-full blur-sm" />
    </button>
  );
}

// Level detail popup
function LevelDetailPopup({ level, isCompleted, stars, bestScore, canPlay, onPlay, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm modal-backdrop-enter" />
      
      <Card 
        className="relative w-full max-w-sm p-6 bg-card border-2 border-primary/30 shadow-2xl modal-enter"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-all duration-200 hover:scale-110"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent mb-3 shadow-lg">
            <span className="text-5xl">{level.targetEmoji}</span>
          </div>
          <h3 className="font-display text-2xl font-bold text-foreground">
            Level {level.id}
          </h3>
          <p className="font-body text-muted-foreground">{level.subtitle}</p>
          <Badge className="mt-2">{level.difficulty}</Badge>
        </div>

        <div className="flex justify-center gap-2 mb-4">
          {[1, 2, 3].map((star) => (
            <Star
              key={star}
              className={`w-10 h-10 ${
                star <= stars 
                  ? 'text-warning fill-warning drop-shadow-lg' 
                  : 'text-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        <div className="space-y-2 mb-4 p-3 bg-muted/50 rounded-xl">
          <div className="flex items-center justify-between">
            <span className="font-body text-sm text-muted-foreground">Target:</span>
            <span className="font-display font-semibold">{level.targetCount} {level.targetEmoji}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-body text-sm text-muted-foreground">Moves:</span>
            <span className="font-display font-semibold">{level.moves}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-body text-sm text-muted-foreground">Grid:</span>
            <span className="font-display font-semibold">{level.boardSize}x{level.boardSize}</span>
          </div>
        </div>

        {bestScore > 0 && (
          <p className="text-center font-body text-sm text-muted-foreground mb-4">
            🏆 Best: <span className="font-semibold text-primary">{bestScore.toLocaleString()}</span> pts
          </p>
        )}

        <Button 
          onClick={onPlay}
          disabled={!canPlay}
          size="lg"
          className="w-full font-display font-bold text-lg shadow-lg btn-hover-effect"
        >
          <Play className="w-6 h-6 mr-2" />
          {isCompleted ? 'Play Again' : 'Play Level'}
        </Button>

        {!canPlay && (
          <p className="text-center mt-3 font-body text-sm text-destructive">
            <Heart className="w-4 h-4 inline mr-1" />
            Not enough HP to play
          </p>
        )}
      </Card>
    </div>
  );
}

export default function LevelMapPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hp, maxHP, hpPercentage, canPlay, getRegenInfo, healHP } = useHealth();
  const { totalXP, level, currentXP, neededXP, prestige } = useXP();
  const { getChallengesCounts } = useChallenges();
  const { isPlaying: isMusicPlaying, musicEnabled, toggleMusic, fadeInMusic } = useMusicSettings();
  const { 
    totalStars,
    totalCoins,
    inventory,
    collectedFruits,
    equipment,
    equippedItems,
    permanentUpgrades,
    battlePass,
    buyConsumable, 
    buyPermanentUpgrade,
    addStars,
    addToInventory,
    refreshShopData,
    equipEquipment,
    unequipEquipment,
    rechargeEquipment,
    consumeItem
  } = useShop();
  const { addXP } = useXP();
  const completedLevels = getCompletedLevels();
  const challengeCounts = getChallengesCounts(prestige);
  
  // Prestige emblem helper (Roman numerals with colors)
  const getPrestigeEmblemDisplay = (prestigeLevel) => {
    if (prestigeLevel >= 6) {
      return { symbol: 'M', bgClass: 'bg-gradient-to-r from-amber-400 to-yellow-300', textClass: 'text-amber-900' };
    }
    const emblems = {
      1: { symbol: 'I', bgClass: 'bg-emerald-500', textClass: 'text-white' },
      2: { symbol: 'II', bgClass: 'bg-sky-500', textClass: 'text-white' },
      3: { symbol: 'III', bgClass: 'bg-violet-500', textClass: 'text-white' },
      4: { symbol: 'IV', bgClass: 'bg-rose-500', textClass: 'text-white' },
      5: { symbol: 'V', bgClass: 'bg-gradient-to-r from-cyan-400 to-purple-500', textClass: 'text-white' }
    };
    return emblems[prestigeLevel] || null;
  };
  
  // State for inventory modal
  const [showInventory, setShowInventory] = useState(false);
  
  // API URL for fetching data
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Wheel of Fortune
  const { 
    isOpen: showWheel, 
    openWheel, 
    closeWheel, 
    lastSpinDate, 
    canSpinToday, 
    recordSpin 
  } = useWheelOfFortune();
  
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasManuallyNavigated, setHasManuallyNavigated] = useState(false); // Track if user manually navigated to a world
  const [showShop, setShowShop] = useState(false);
  const [shopInitialTab, setShopInitialTab] = useState('consumables'); // Track which tab to open
  const [showSettings, setShowSettings] = useState(false);
  const [showWorldSelector, setShowWorldSelector] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showProfileSidebar, setShowProfileSidebar] = useState(false); // New profile sidebar
  const [showBattlePass, setShowBattlePass] = useState(false);
  const [showCosmetics, setShowCosmetics] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [showExchange, setShowExchange] = useState(false); // Grand Exchange modal
  const [showChat, setShowChat] = useState(false); // Chat modal
  const [showSocialMenu, setShowSocialMenu] = useState(false); // Social menu popup
  const [showMembership, setShowMembership] = useState(false); // Premium membership modal
  const [membershipStatus, setMembershipStatus] = useState(null); // Premium membership status
  const [pageTransition, setPageTransition] = useState('');
  const [activeNavTab, setActiveNavTab] = useState('map'); // Bottom nav active tab
  const [showTutorial, setShowTutorial] = useState(false); // Tutorial overlay
  const [forceTutorial, setForceTutorial] = useState(false); // Force show from Help menu
  const [showPvP, setShowPvP] = useState(false); // PvP Arena modal
  const [showProfileSwitcher, setShowProfileSwitcher] = useState(false); // Profile switcher
  const [showDailyReward, setShowDailyReward] = useState(false); // Daily reward modal
  const [showWorldLore, setShowWorldLore] = useState(null); // World lore popup
  const [activeEvents, setActiveEvents] = useState([]); // Limited-time events
  const mapRef = useRef(null);
  
  // Check if new user needs tutorial
  useEffect(() => {
    const tutorialComplete = localStorage.getItem('fruitCrush_tutorialComplete');
    if (!tutorialComplete) {
      setShowTutorial(true);
    }
  }, []);

  // Fetch active limited-time events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/active`);
        if (res.ok) {
          const data = await res.json();
          setActiveEvents(data.events || []);
        }
      } catch (e) { /* silent */ }
    };
    fetchEvents();
  }, []);

  // PvP play event listener
  useEffect(() => {
    const handlePvPPlay = (e) => {
      const { challengeId, boardSeed, levelId, wager, role } = e.detail;
      navigate(`/play/${levelId}`, { state: { pvp: { challengeId, boardSeed, wager, role } } });
    };
    window.addEventListener('pvp-play', handlePvPPlay);
    return () => window.removeEventListener('pvp-play', handlePvPPlay);
  }, [navigate]);

  // Auto-show daily reward if available
  useEffect(() => {
    const checkDailyReward = async () => {
      try {
        const token = localStorage.getItem('fruitCrush_token');
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/daily-reward/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.available) {
          setTimeout(() => setShowDailyReward(true), 800);
        }
      } catch { /* silent */ }
    };
    checkDailyReward();
  }, []);
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  
  // Cosmetics state from progress
  const [unlockedSkins, setUnlockedSkins] = useState([]);
  const [unlockedEffects, setUnlockedEffects] = useState([]);
  const [unlockedThemes, setUnlockedThemes] = useState([]);
  const [unlockedFrames, setUnlockedFrames] = useState([]);
  const [equippedSkin, setEquippedSkin] = useState(null);
  const [equippedEffect, setEquippedEffect] = useState(null);
  const [equippedTheme, setEquippedTheme] = useState(null);
  const [equippedFrame, setEquippedFrame] = useState(null);
  
  // Fetch cosmetics from progress
  useEffect(() => {
    const fetchCosmetics = async () => {
      const token = localStorage.getItem('fruitCrush_token');
      if (!token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUnlockedSkins(data.unlocked_skins || []);
          setUnlockedEffects(data.unlocked_effects || []);
          setUnlockedThemes(data.unlocked_themes || []);
          setUnlockedFrames(data.unlocked_frames || []);
          setEquippedSkin(data.equipped_skin || null);
          setEquippedEffect(data.equipped_effect || null);
          setEquippedTheme(data.equipped_theme || null);
          setEquippedFrame(data.equipped_frame || null);
        }
      } catch (error) {
        console.error('Failed to fetch cosmetics:', error);
      }
    };
    fetchCosmetics();
  }, []);
  
  // Handle cosmetic equip
  const handleEquipCosmetic = (type, id) => {
    switch (type) {
      case 'skins': setEquippedSkin(id); break;
      case 'effects': setEquippedEffect(id); break;
      case 'themes': setEquippedTheme(id); break;
      case 'frames': setEquippedFrame(id); break;
    }
  };
  
  // Fetch membership status
  const fetchMembershipStatus = async () => {
    const token = localStorage.getItem('fruitCrush_token');
    if (!token) return;
    
    try {
      // Get user ID from token
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.user_id;
      
      const response = await fetch(`${API_URL}/api/stripe/membership-status/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembershipStatus(data);
        
        // Apply premium health bonus (+500 HP) if user is a premium member
        if (data.is_premium_member) {
          setPremiumHealthBonus(500);
        } else {
          setPremiumHealthBonus(0);
        }
      }
    } catch (error) {
      console.error('Failed to fetch membership status:', error);
    }
  };
  
  useEffect(() => {
    fetchMembershipStatus();
  }, []);
  
  // Battle Pass default state if not loaded
  const battlePassData = battlePass || {
    is_premium: false,
    current_xp: 0,
    current_tier: 0,
    claimed_free: [],
    claimed_premium: [],
    season_number: 1
  };
  
  // Handle Battle Pass reward claim
  const handleClaimBattlePassReward = async (tier, track) => {
    const token = localStorage.getItem('fruitCrush_token');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/battle-pass/claim-reward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tier, track })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Claimed reward:', data);
        
        // Refresh shop data (stars, inventory, battle pass state)
        await refreshShopData();
        
        // Refresh cosmetics data if the reward was a skin/effect/theme/frame
        if (['skin', 'effect', 'theme', 'frame'].includes(data.reward?.type)) {
          const progressResponse = await fetch(`${API_URL}/api/progress`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (progressResponse.ok) {
            const progressData = await progressResponse.json();
            setUnlockedSkins(progressData.unlocked_skins || []);
            setUnlockedEffects(progressData.unlocked_effects || []);
            setUnlockedThemes(progressData.unlocked_themes || []);
            setUnlockedFrames(progressData.unlocked_frames || []);
          }
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to claim reward:', errorData);
        alert(errorData.detail || 'Failed to claim reward');
      }
    } catch (error) {
      console.error('Error claiming reward:', error);
    }
  };
  
  // Handle wheel reward
  const handleWheelReward = (reward) => {
    recordSpin();
    
    switch (reward.type) {
      case 'stars':
        addStars(reward.amount);
        break;
      case 'xp':
        addXP(reward.amount);
        break;
      case 'item':
        addToInventory(reward.item, reward.amount);
        break;
      default:
        break;
    }
  };
  
  // Handle Stripe payment verification on page load (after redirect from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const starsParam = urlParams.get('stars');
    
    if (sessionId && starsParam) {
      // Verify the payment and add stars
      const verifyPayment = async () => {
        try {
          const response = await fetch(`${API_URL}/api/stripe/verify-session/${sessionId}`);
          const data = await response.json();
          
          if (data.success && data.stars > 0) {
            addStars(data.stars);
            // Refresh shop data to update UI
            await refreshShopData();
            alert(`🎉 Payment successful! ${data.stars} stars have been added to your account!`);
          } else {
            console.log('Payment verification:', data);
          }
        } catch (error) {
          console.error('Payment verification failed:', error);
        }
      };
      
      verifyPayment();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [API_URL, addStars, refreshShopData]);
  
  // World theme configurations
  const WORLD_THEMES = {
    1: { // Fruit Garden (1-50)
      background: 'linear-gradient(to bottom, #87CEEB 0%, #90EE90 60%, #228B22 100%)',
      lore: 'Welcome to the Fruit Garden, where your journey begins! Ripe fruits hang from every branch, waiting to be matched.',
      decorations: {
        topLeft: '☁️', topRight: '☁️',
        trees: ['🌲', '🌳', '🌴', '🌲'],
        flowers: ['🌸', '🌺', '🦋'],
        startText: 'START 🚩'
      }
    },
    2: { // Ice World (51-60)
      background: 'linear-gradient(to bottom, #E0F7FA 0%, #B2EBF2 40%, #4DD0E1 70%, #0097A7 100%)',
      lore: 'The Ice World freezes everything in sight. Chains of ice bind the fruits — only swift matches can break them free.',
      decorations: {
        topLeft: '❄️', topRight: '❄️',
        trees: ['🌲', '🏔️', '⛰️', '🌲'],
        flowers: ['❄️', '💎', '✨'],
        startText: 'ICE WORLD ❄️'
      }
    },
    3: { // Chain Realm (61-70)
      background: 'linear-gradient(to bottom, #455A64 0%, #37474F 40%, #263238 70%, #1C313A 100%)',
      lore: 'Deep within the Chain Realm, ancient gears turn endlessly. Here, linked chains bind the fruits in pairs — match wisely.',
      decorations: {
        topLeft: '⚙️', topRight: '⚙️',
        trees: ['🏭', '⚙️', '🔧', '🏗️'],
        flowers: ['⛓️', '🔒', '🔑'],
        startText: 'CHAIN REALM ⛓️'
      }
    },
    4: { // Chocolate Factory (71-80)
      background: 'linear-gradient(to bottom, #FFCC80 0%, #FF9800 40%, #E65100 70%, #BF360C 100%)',
      lore: 'The Chocolate Factory oozes with molten cocoa. Chocolate spreads each turn — clear it before it takes over!',
      decorations: {
        topLeft: '☁️', topRight: '☁️',
        trees: ['🍫', '🧁', '🎂', '🍪'],
        flowers: ['🍬', '🍭', '🍩'],
        startText: 'CHOCOLATE 🍫'
      }
    },
    5: { // Honey Hive (81-90)
      background: 'linear-gradient(to bottom, #FFF8E1 0%, #FFE082 40%, #FFC107 70%, #FF8F00 100%)',
      lore: 'Buzzing bees guard their golden treasure. Honey tiles slow your matches — create big combos to break through!',
      decorations: {
        topLeft: '🐝', topRight: '🐝',
        trees: ['🌻', '🌼', '🌺', '🌸'],
        flowers: ['🍯', '🐝', '🌾'],
        startText: 'HONEY HIVE 🍯'
      }
    },
    6: { // Lava World (91-100)
      background: 'linear-gradient(to bottom, #1a0a00 0%, #4a1500 20%, #8B0000 40%, #FF4500 60%, #FF6B00 80%, #FFD700 100%)',
      lore: 'Flames roar in the Lava World! Fire tiles ignite the board — match fast before the heat consumes everything.',
      decorations: {
        topLeft: '🌋', topRight: '🔥',
        trees: ['🌋', '🔥', '💎', '☄️'],
        flowers: ['🔥', '💀', '⚡'],
        startText: 'LAVA WORLD 🌋'
      }
    },
    7: { // Point World (101-120)
      background: 'linear-gradient(to bottom, #4A148C 0%, #7B1FA2 30%, #9C27B0 60%, #E040FB 100%)',
      lore: 'In the Point World, every match is magnified. Score multipliers appear on tiles — hit them for massive points!',
      decorations: {
        topLeft: '🎯', topRight: '🎯',
        trees: ['🏆', '💎', '🎖️', '🥇'],
        flowers: ['✨', '💫', '⭐'],
        startText: 'POINT WORLD 🎯'
      }
    },
    8: { // Dark Forest (121-140)
      background: 'linear-gradient(to bottom, #0a0a0a 0%, #1a1a2e 15%, #16213e 30%, #0f3d3e 50%, #1a472a 70%, #0d1f0d 100%)',
      lore: 'Shadows twist between ancient trees in the Dark Forest. Visibility is limited — only nearby fruits reveal themselves.',
      decorations: {
        topLeft: '🌲', topRight: '🦉',
        trees: ['🌲', '🌳', '🍂', '🌿'],
        flowers: ['🍄', '🦇', '🕷️'],
        startText: 'DARK FOREST 🌲'
      }
    },
    9: { // Ocean Depths (141-160)
      background: 'linear-gradient(to bottom, #0077B6 0%, #023E8A 20%, #03045E 40%, #000033 60%, #000022 80%, #000011 100%)',
      lore: 'Descend into the Ocean Depths where currents shift the board. Coral obstacles grow each turn — keep the waters clear!',
      decorations: {
        topLeft: '🌊', topRight: '🐙',
        trees: ['🪸', '🌿', '🐚', '🦪'],
        flowers: ['🫧', '🐠', '🐟'],
        startText: 'OCEAN DEPTHS 🌊'
      }
    },
    10: { // Frozen Peaks (161-180)
      background: 'linear-gradient(to bottom, #E8F4FF 0%, #B8D4E8 15%, #7EB8DA 30%, #4A9AC9 50%, #2E6B8A 70%, #1C4966 85%, #0F2840 100%)',
      lore: 'Atop the Frozen Peaks, blizzards rage and ice crystals form. Snow covers tiles — match through the frost before you freeze!',
      decorations: {
        topLeft: '🏔️', topRight: '❄️',
        trees: ['🏔️', '⛰️', '🌲', '🗻'],
        flowers: ['❄️', '🌨️', '💎'],
        startText: 'FROZEN PEAKS ❄️'
      }
    },
    11: { // Ancient Ruins (181-200)
      background: 'linear-gradient(to bottom, #8B7355 0%, #6B5344 15%, #5D4037 30%, #4E342E 50%, #3E2723 70%, #1C1410 100%)',
      lore: 'Crumbling temples hide forgotten power. Stone idols block your path — break the ancient seals to reclaim the ruins.',
      decorations: {
        topLeft: '🏛️', topRight: '🗿',
        trees: ['🏛️', '🗿', '⚱️', '🏺'],
        flowers: ['🪲', '👁️', '🔥'],
        startText: 'ANCIENT RUINS 🏛️'
      }
    },
    12: { // Volcanic Core (201-220)
      background: 'linear-gradient(to bottom, #1a0505 0%, #3d0a0a 15%, #6B0000 30%, #8B0000 50%, #B22222 70%, #FF4500 85%, #FF6B00 100%)',
      lore: 'You have reached the Volcanic Core. Molten lava rises from below — match quickly or be consumed by the inferno!',
      decorations: {
        topLeft: '🔥', topRight: '🌋',
        trees: ['🌋', '🔥', '💎', '☄️'],
        flowers: ['🔥', '💀', '⚡'],
        startText: 'VOLCANIC CORE 🔥'
      }
    },
    13: { // Moonlight Garden (221-240)
      background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 20%, #1f3a5f 40%, #2d4a6f 55%, #3d5a8f 70%, #4d6a9f 85%, #6366f1 100%)',
      lore: 'Under the silver moon, the Moonlight Garden shifts between day and night. Fruits change with the cycle — adapt or fail.',
      decorations: {
        topLeft: '🌙', topRight: '✨',
        trees: ['🌙', '🌸', '🌺', '🌿'],
        flowers: ['✨', '💫', '🦋'],
        startText: 'MOONLIGHT 🌙'
      }
    },
    14: { // Alchemist's Lab (241-260)
      background: 'linear-gradient(to bottom, #0d1b0d 0%, #1a3a1a 15%, #2d5a2d 30%, #10b981 50%, #34d399 70%, #6ee7b7 85%, #a7f3d0 100%)',
      lore: 'Bubbling potions and glowing elixirs fill the Alchemist\'s Lab. Combine rare fruits to transmute them into gold!',
      decorations: {
        topLeft: '⚗️', topRight: '🧪',
        trees: ['⚗️', '🧪', '🔮', '📜'],
        flowers: ['✨', '💎', '🌟'],
        startText: 'ALCHEMIST LAB ⚗️'
      }
    },
    15: { // Circus Carnival (261-280)
      background: 'linear-gradient(to bottom, #831843 0%, #be185d 15%, #db2777 30%, #ec4899 45%, #f472b6 60%, #fbcfe8 80%, #fce7f3 100%)',
      lore: 'Step right up to the Circus Carnival! Spinning wheels, bouncing balls, and surprise tiles keep every match unpredictable.',
      decorations: {
        topLeft: '🎪', topRight: '🎡',
        trees: ['🎪', '🎠', '🎡', '🎢'],
        flowers: ['🎈', '🎉', '🎀'],
        startText: 'CIRCUS 🎪'
      }
    },
    16: { // Pirate Shipwreck (281-300)
      background: 'linear-gradient(to bottom, #1c1917 0%, #292524 15%, #44403c 30%, #57534e 45%, #78716c 60%, #a8a29e 80%, #d6d3d1 100%)',
      lore: 'Among the sunken timbers of the Pirate Shipwreck, treasure chests lie buried. Dig through barnacles to find the loot!',
      decorations: {
        topLeft: '🏴‍☠️', topRight: '⚓',
        trees: ['🚢', '⚓', '🦜', '🗺️'],
        flowers: ['💰', '💎', '🔱'],
        startText: 'SHIPWRECK 🏴‍☠️'
      }
    },
    17: { // Cherry Blossom Temple (301-320)
      background: 'linear-gradient(to bottom, #fce7f3 0%, #fbcfe8 15%, #f9a8d4 30%, #f472b6 50%, #ec4899 70%, #be185d 90%, #831843 100%)',
      lore: 'Petals drift through the Cherry Blossom Temple. Sacred fruits rest on altar tiles — honor the temple to unlock their power.',
      decorations: {
        topLeft: '🌸', topRight: '⛩️',
        trees: ['🌸', '⛩️', '🎋', '🎍'],
        flowers: ['🌺', '🦢', '🪷'],
        startText: 'TEMPLE 🌸'
      }
    },
    18: { // Storm Kingdom (321-340)
      background: 'linear-gradient(to bottom, #0c0c0c 0%, #1f1f1f 10%, #2d2d2d 20%, #3d3d3d 35%, #4a4a4a 50%, #6b6b6b 70%, #facc15 90%, #fef08a 100%)',
      lore: 'Lightning splits the sky over the Storm Kingdom. Thunder strikes random tiles each turn — dodge the bolts and crush on!',
      decorations: {
        topLeft: '⚡', topRight: '🌩️',
        trees: ['⚡', '🌩️', '🏰', '🗡️'],
        flowers: ['💫', '✨', '⭐'],
        startText: 'STORM ⚡'
      }
    },
    19: { // Mushroom Cavern (341-360)
      background: 'linear-gradient(to bottom, #1a0a2e 0%, #2d1b4e 15%, #4c1d95 30%, #6d28d9 45%, #7c3aed 60%, #8b5cf6 75%, #a855f7 90%, #c084fc 100%)',
      lore: 'Bioluminescent mushrooms light the Mushroom Cavern. Spores spread each move, transforming fruits — embrace the chaos!',
      decorations: {
        topLeft: '🍄', topRight: '🔮',
        trees: ['🍄', '🌿', '🔮', '💜'],
        flowers: ['✨', '💎', '🦋'],
        startText: 'CAVERN 🍄'
      }
    },
    20: { // Casino Royale (361-380)
      background: 'linear-gradient(to bottom, #0c0c0c 0%, #1a1a1a 15%, #2a2a2a 30%, #3a3a3a 45%, #fbbf24 60%, #f59e0b 75%, #d97706 90%, #b45309 100%)',
      lore: 'Welcome to Casino Royale where luck meets skill. Random multipliers appear on the board — bet big, score bigger!',
      decorations: {
        topLeft: '🎰', topRight: '🎲',
        trees: ['🎰', '🃏', '💎', '🎲'],
        flowers: ['💰', '🏆', '👑'],
        startText: 'CASINO 🎰'
      }
    },
    21: { // Cosmic Nebula (381-400)
      background: 'linear-gradient(to bottom, #0a0015 0%, #1a0030 10%, #2d0050 25%, #4c1d95 40%, #6d28d9 55%, #7c3aed 70%, #a855f7 85%, #e879f9 100%)',
      lore: 'At the edge of the Cosmic Nebula, gravity itself bends. Fruits shift direction unpredictably — master the cosmos!',
      decorations: {
        topLeft: '🌌', topRight: '🚀',
        trees: ['🌌', '🚀', '🪐', '⭐'],
        flowers: ['✨', '💫', '🌟'],
        startText: 'COSMOS 🌌'
      }
    },
    // === MEMBERSHIP EXCLUSIVE WORLDS ===
    99: { // Crystal Paradise (9001-9050) - Member Exclusive - "Diamond Sky Palace"
      background: 'linear-gradient(to bottom, #fdf4ff 0%, #e0f2fe 10%, #bae6fd 25%, #7dd3fc 40%, #38bdf8 55%, #0ea5e9 70%, #a855f7 85%, #7c3aed 100%)',
      decorations: {
        topLeft: '💎', topRight: '✨',
        trees: ['💎', '☁️', '🌈', '⭐'],
        flowers: ['💠', '✨', '💫'],
        startText: '💎 DIAMOND SKY PALACE 💎'
      }
    },
    100: { // Royal Citadel (8001-8050) - VIP Elite 12x12 - "Throne Room of Ages"
      background: 'linear-gradient(to bottom, #1c1917 0%, #292524 10%, #44403c 25%, #78716c 40%, #a8a29e 50%, #d4af37 65%, #fbbf24 80%, #fef3c7 100%)',
      decorations: {
        topLeft: '👑', topRight: '🏰',
        trees: ['🏰', '🪟', '🕯️', '🏆'],
        flowers: ['💰', '👑', '💎'],
        startText: '👑 THRONE ROOM OF AGES 👑'
      }
    }
  };
  
  // Get current world based on page
  const getCurrentWorld = () => {
    const firstLevelOnPage = currentPage * 10;
    const level = LEVELS[firstLevelOnPage];
    
    // If the level exists, check its ID to determine world
    if (level) {
      const levelId = level.id;
      // Membership worlds
      if (levelId >= 9001 && levelId <= 9050) return 99; // Crystal Paradise
      if (levelId >= 8001 && levelId <= 8050) return 100; // Royal Citadel
      // Regular worlds
      if (levelId <= 50) return 1;   // Fruit Garden
      if (levelId <= 60) return 2;   // Ice World
      if (levelId <= 70) return 3;   // Chain Realm
      if (levelId <= 80) return 4;   // Chocolate Factory
      if (levelId <= 90) return 5;   // Honey Hive
      if (levelId <= 100) return 6;  // Lava World
      if (levelId <= 120) return 7;  // Point World
      if (levelId <= 140) return 8;  // Dark Forest
      if (levelId <= 160) return 9;  // Ocean Depths
      if (levelId <= 180) return 10; // Frozen Peaks
      if (levelId <= 200) return 11; // Ancient Ruins
      if (levelId <= 220) return 12; // Volcanic Core
      if (levelId <= 240) return 13; // Moonlight Garden
      if (levelId <= 260) return 14; // Alchemist's Lab
      if (levelId <= 280) return 15; // Circus Carnival
      if (levelId <= 300) return 16; // Pirate Shipwreck
      if (levelId <= 320) return 17; // Cherry Blossom Temple
      if (levelId <= 340) return 18; // Storm Kingdom
      if (levelId <= 360) return 19; // Mushroom Cavern
      if (levelId <= 380) return 20; // Casino Royale
      if (levelId <= 400) return 21; // Cosmic Nebula
    }
    return 1; // Default to Fruit Garden
  };
  
  const currentWorld = getCurrentWorld();
  const worldTheme = WORLD_THEMES[currentWorld];

  // Show world lore on first visit to a new world
  useEffect(() => {
    if (currentWorld) {
      const seenWorlds = JSON.parse(localStorage.getItem('fruitCrush_seenWorlds') || '[]');
      if (!seenWorlds.includes(currentWorld) && worldTheme?.lore) {
        setShowWorldLore({ world: currentWorld, lore: worldTheme.lore, name: worldTheme?.decorations?.startText || 'New World' });
        seenWorlds.push(currentWorld);
        localStorage.setItem('fruitCrush_seenWorlds', JSON.stringify(seenWorlds));
      }
    }
  }, [currentWorld, worldTheme]);

  // Get current level (first incomplete level)
  const currentLevel = completedLevels.length > 0 
    ? Math.max(...completedLevels) + 1 
    : 1;
  
  // Equipped sword state (stored in localStorage)
  const [equippedSword, setEquippedSword] = useState(() => {
    const saved = localStorage.getItem('fruitCrush_equippedSword');
    return saved || null;
  });
  
  // Calculate available sword based on highest completed game level and auto-equip best if none selected
  useEffect(() => {
    if (!equippedSword && currentLevel > 0) {
      let bestSword = null;
      // Sword unlocks based on GAME LEVEL completed (not XP level)
      if (currentLevel >= 150) bestSword = 'master_sword';
      else if (currentLevel >= 100) bestSword = 'diamond_sword';
      else if (currentLevel >= 50) bestSword = 'golden_sword';
      else if (currentLevel >= 20) bestSword = 'silver_sword';
      else if (currentLevel >= 3) bestSword = 'bronze_sword';
      
      if (bestSword) {
        setEquippedSword(bestSword);
        localStorage.setItem('fruitCrush_equippedSword', bestSword);
      }
    }
  }, [currentLevel, equippedSword]);
  
  // Handle sword equip
  const handleEquipSword = (swordId) => {
    setEquippedSword(swordId);
    localStorage.setItem('fruitCrush_equippedSword', swordId);
  };
  
  // Fade in music when returning to level map
  useEffect(() => {
    if (musicEnabled && !isMusicPlaying) {
      fadeInMusic(1000); // 1 second fade in
    }
  }, []); // Only run on mount
  
  // Show 10 levels per page
  const LEVELS_PER_PAGE = 10;
  const totalPages = Math.ceil(LEVELS.length / LEVELS_PER_PAGE);
  const currentLevels = LEVELS.slice(currentPage * LEVELS_PER_PAGE, (currentPage + 1) * LEVELS_PER_PAGE);
  
  // Handle world selection - navigate to the first level of that world
  const handleWorldSelect = (world) => {
    // Calculate page for world
    let pageForWorld;
    
    if (world.memberExclusive) {
      // Membership worlds are at the end of LEVELS array
      // Regular levels: 1-400 (indices 0-399, pages 0-39)
      // Royal Citadel: 8001-8050 (indices 400-449, pages 40-44)
      // Crystal Paradise: 9001-9050 (indices 450-499, pages 45-49)
      if (world.id === 100) { // Royal Citadel
        pageForWorld = 40;
      } else if (world.id === 99) { // Crystal Paradise
        pageForWorld = 45;
      } else {
        pageForWorld = 0;
      }
    } else {
      // Regular worlds: calculate page from level number
      pageForWorld = Math.floor((world.levels[0] - 1) / LEVELS_PER_PAGE);
    }
    
    setCurrentPage(pageForWorld);
    setHasManuallyNavigated(true);
    // Save to sessionStorage so it persists after playing levels
    sessionStorage.setItem('fruitCrush_traveledPage', pageForWorld.toString());
    setShowWorldSelector(false);
  };
  
  // Handle sidebar menu selection
  const handleMenuSelect = (menuId) => {
    switch (menuId) {
      case 'worlds':
      case 'map':
        setShowWorldSelector(true);
        break;
      case 'shop':
        setShopInitialTab('consumables');
        setShowShop(true);
        break;
      case 'exchange':
        setShowExchange(true);
        break;
      case 'battle-pass':
        setShowBattlePass(true);
        break;
      case 'cosmetics':
        setShowCosmetics(true);
        break;
      case 'friends':
        setShowFriends(true);
        break;
      case 'chat':
        setShowChat(true);
        break;
      case 'daily-spin':
        openWheel();
        break;
      case 'settings':
        setShowSettings(true);
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'codex':
        navigate('/codex');
        break;
      case 'crafting':
        navigate('/crafting');
        break;
      case 'leaderboard':
        navigate('/leaderboard');
        break;
      case 'challenges':
        // Navigate to full challenges page
        navigate('/challenges');
        break;
      case 'profile':
        setShowProfileSidebar(true);
        break;
      case 'social':
        setShowSocialMenu(true);
        break;
      case 'guild':
        navigate('/guild');
        break;
      case 'home':
        // Show progress modal
        setShowProgressModal(true);
        break;
      case 'membership':
        setShowMembership(true);
        break;
      case 'tutorial':
        setForceTutorial(true);
        setShowTutorial(true);
        break;
      case 'story':
        navigate('/story');
        break;
      case 'pvp':
        setShowPvP(true);
        break;
      case 'switch-profile':
        setShowProfileSwitcher(true);
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  // Handle bottom navigation tab change
  const handleNavTabChange = (tabId) => {
    setActiveNavTab(tabId);
    switch (tabId) {
      case 'map':
        // Open world selector
        setShowWorldSelector(true);
        break;
      case 'shop':
        setShopInitialTab('consumables');
        setShowShop(true);
        break;
      case 'exchange':
        setShowExchange(true);
        break;
      case 'social':
        // Show social menu with Friends, Leaderboard, Chat options
        setShowSocialMenu(true);
        break;
      case 'guild':
        navigate('/guild');
        break;
      case 'profile':
        setShowProfileSidebar(true);
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'codex':
        navigate('/codex');
        break;
      case 'crafting':
        navigate('/crafting');
        break;
      case 'leaderboard':
        navigate('/leaderboard');
        break;
      case 'challenges':
        navigate('/challenges');
        break;
      default:
        break;
    }
  };

  // Handle social menu option selection
  const handleSocialOption = (optionId) => {
    switch (optionId) {
      case 'friends':
        setShowFriends(true);
        break;
      case 'leaderboard':
        navigate('/leaderboard');
        break;
      case 'chat':
        setShowChat(true);
        break;
      default:
        break;
    }
  };

  // PATH CONFIGURATIONS: Diverse path shapes per page that connect seamlessly
  // Each page has a unique path pattern, but start/end points match for continuity
  const PATH_PATTERNS = {
    // Page 0 (Levels 1-10): Classic S-curve, starts bottom-left, ends top-right
    0: { startX: 25, endX: 75, pattern: 'sCurve' },
    // Page 1 (Levels 11-20): Reverse S-curve, starts bottom-right (connects to page 0), ends top-left
    1: { startX: 75, endX: 25, pattern: 'sCurveReverse' },
    // Page 2 (Levels 21-30): Double wave, starts bottom-left (connects to page 1), ends top-right
    2: { startX: 25, endX: 75, pattern: 'doubleWave' },
    // Page 3 (Levels 31-40): Zigzag, starts bottom-right (connects to page 2), ends top-left
    3: { startX: 75, endX: 25, pattern: 'zigzag' },
    // Page 4 (Levels 41-50): Spiral curve, starts bottom-left (connects to page 3), ends top-right
    4: { startX: 25, endX: 75, pattern: 'spiral' },
    // Page 5 (Levels 51-60): Wide swoosh, starts bottom-right (connects to page 4), ends top-left
    5: { startX: 75, endX: 25, pattern: 'swoosh' },
    // Page 6 (Levels 61-70): Triple wave, starts bottom-left (connects to page 5), ends top-right
    6: { startX: 25, endX: 75, pattern: 'tripleWave' },
    // Page 7 (Levels 71-80): Mountain path, starts bottom-right (connects to page 6), ends top-left
    7: { startX: 75, endX: 25, pattern: 'mountain' },
    // Page 8 (Levels 81-90): River curve, starts bottom-left (connects to page 7), ends top-right
    8: { startX: 25, endX: 75, pattern: 'river' },
    // Page 9 (Levels 91-100): Grand finale spiral, starts bottom-right (connects to page 8), ends top-left
    9: { startX: 75, endX: 25, pattern: 'grandFinale' },
    // Page 10 (Levels 101-110): Diamond path, starts bottom-left (connects to page 9), ends top-right
    10: { startX: 25, endX: 75, pattern: 'diamond' },
    // Page 11 (Levels 111-120): Helix pattern, starts bottom-right (connects to page 10), ends top-left
    11: { startX: 75, endX: 25, pattern: 'helix' },
    // Page 12 (Levels 121-130): Forest vine path, starts bottom-left (connects to page 11), ends top-right
    12: { startX: 25, endX: 75, pattern: 'forestVine' },
    // Page 13 (Levels 131-140): Dark spiral, starts bottom-right (connects to page 12), ends top-left
    13: { startX: 75, endX: 25, pattern: 'darkSpiral' },
    // === OCEAN DEPTHS (141-160) ===
    // Page 14 (Levels 141-150): Ocean wave, starts bottom-left (connects to page 13), ends top-right
    14: { startX: 25, endX: 75, pattern: 'oceanWave' },
    // Page 15 (Levels 151-160): Deep dive, starts bottom-right (connects to page 14), ends top-left
    15: { startX: 75, endX: 25, pattern: 'deepDive' },
    // === FROZEN PEAKS (161-180) ===
    // Page 16 (Levels 161-170): Mountain climb, starts bottom-left (connects to page 15), ends top-right
    16: { startX: 25, endX: 75, pattern: 'mountainClimb' },
    // Page 17 (Levels 171-180): Glacier path, starts bottom-right (connects to page 16), ends top-left
    17: { startX: 75, endX: 25, pattern: 'glacierPath' },
    // === ANCIENT RUINS (181-200) ===
    // Page 18 (Levels 181-190): Pyramid steps, starts bottom-left (connects to page 17), ends top-right
    18: { startX: 25, endX: 75, pattern: 'pyramidSteps' },
    // Page 19 (Levels 191-200): Temple spiral, starts bottom-right (connects to page 18), ends top-left
    19: { startX: 75, endX: 25, pattern: 'templeSpiral' },
    // === NEW WORLDS (201-400) - Pages 20-39 ===
    20: { startX: 25, endX: 75, pattern: 'volcanicRise' },
    21: { startX: 75, endX: 25, pattern: 'magmaFlow' },
    22: { startX: 25, endX: 75, pattern: 'moonlightPath' },
    23: { startX: 75, endX: 25, pattern: 'lunarOrbit' },
    24: { startX: 25, endX: 75, pattern: 'alchemySpiral' },
    25: { startX: 75, endX: 25, pattern: 'potionBubble' },
    26: { startX: 25, endX: 75, pattern: 'circusTent' },
    27: { startX: 75, endX: 25, pattern: 'carnivalLoop' },
    28: { startX: 25, endX: 75, pattern: 'shipwreckPath' },
    29: { startX: 75, endX: 25, pattern: 'treasureTrail' },
    30: { startX: 25, endX: 75, pattern: 'blossomWind' },
    31: { startX: 75, endX: 25, pattern: 'templeBridge' },
    32: { startX: 25, endX: 75, pattern: 'lightningBolt' },
    33: { startX: 75, endX: 25, pattern: 'thunderCloud' },
    34: { startX: 25, endX: 75, pattern: 'mushroomPath' },
    35: { startX: 75, endX: 25, pattern: 'cavernDepths' },
    36: { startX: 25, endX: 75, pattern: 'casinoChip' },
    37: { startX: 75, endX: 25, pattern: 'luckyStreak' },
    38: { startX: 25, endX: 75, pattern: 'cosmicWarp' },
    39: { startX: 75, endX: 25, pattern: 'nebulaSpiral' },
    // === ROYAL CITADEL (VIP WORLD) - Pages 40-44 ===
    40: { startX: 30, endX: 70, pattern: 'throneAscent' },     // Royal approach
    41: { startX: 70, endX: 30, pattern: 'royalCorridor' },    // Grand corridor
    42: { startX: 30, endX: 70, pattern: 'crownPath' },        // Crown-shaped path
    43: { startX: 70, endX: 30, pattern: 'throneSpiral' },     // Spiral to throne
    44: { startX: 30, endX: 70, pattern: 'royalFinale' },      // Final ascent
    // === CRYSTAL PARADISE (MEMBER WORLD) - Pages 45-49 ===
    45: { startX: 20, endX: 80, pattern: 'diamondRise' },      // Rising diamonds
    46: { startX: 80, endX: 20, pattern: 'crystalCascade' },   // Cascading crystals
    47: { startX: 20, endX: 80, pattern: 'prismPath' },        // Prismatic path
    48: { startX: 80, endX: 20, pattern: 'skyPalaceSpiral' },  // Sky palace spiral
    49: { startX: 20, endX: 80, pattern: 'celestialAscent' },  // Final celestial rise
  };

  // Calculate position on path for a specific progress value (0-1)
  const calculatePathPoint = (progress, pageNum) => {
    const config = PATH_PATTERNS[pageNum] || PATH_PATTERNS[0];
    const { startX, endX, pattern } = config;
    
    // Y is always from bottom (92%) to top (8%)
    const y = 92 - (progress * 84);
    
    // Base X interpolation from start to end
    const baseX = startX + (endX - startX) * progress;
    
    // Apply pattern-specific X modifications
    let x = baseX;
    const amplitude = 20;
    
    switch (pattern) {
      case 'sCurve':
        // Classic S-curve with sine wave
        x = baseX + Math.sin(progress * Math.PI * 2) * amplitude * 0.5;
        break;
      
      case 'sCurveReverse':
        // Reverse S-curve
        x = baseX - Math.sin(progress * Math.PI * 2) * amplitude * 0.5;
        break;
      
      case 'doubleWave':
        // Two complete sine waves
        x = baseX + Math.sin(progress * Math.PI * 4) * amplitude * 0.4;
        break;
      
      case 'zigzag':
        // Sharp zigzag pattern (5 turns)
        const zigzagPhase = (progress * 5) % 1;
        const zigzagDir = Math.floor(progress * 5) % 2 === 0 ? 1 : -1;
        x = baseX + (zigzagPhase * 2 - 1) * amplitude * 0.6 * zigzagDir;
        break;
      
      case 'spiral':
        // Expanding spiral effect
        const spiralPhase = progress * Math.PI * 3;
        const spiralRadius = amplitude * 0.3 * (1 + progress * 0.5);
        x = baseX + Math.sin(spiralPhase) * spiralRadius;
        break;
      
      case 'swoosh':
        // Wide swooping curve
        x = baseX + Math.sin(progress * Math.PI) * amplitude * 0.8;
        break;
      
      case 'tripleWave':
        // Three gentle waves
        x = baseX + Math.sin(progress * Math.PI * 6) * amplitude * 0.35;
        break;
      
      case 'mountain':
        // Mountain peak pattern (up and down)
        const mountainPhase = progress < 0.5 
          ? progress * 2 
          : 2 - progress * 2;
        x = baseX + (mountainPhase - 0.5) * amplitude * 1.2;
        break;
      
      case 'river':
        // Smooth river meander
        x = baseX + Math.sin(progress * Math.PI * 2.5) * amplitude * 0.5 
                  + Math.sin(progress * Math.PI * 5) * amplitude * 0.15;
        break;
      
      case 'grandFinale':
        // Dramatic finale with multiple elements
        x = baseX + Math.sin(progress * Math.PI * 3) * amplitude * 0.4
                  + Math.cos(progress * Math.PI * 6) * amplitude * 0.2;
        break;
      
      case 'diamond':
        // Diamond/rhombus shape path
        const diamondPhase = progress * 4;
        const diamondSegment = Math.floor(diamondPhase) % 4;
        const diamondT = diamondPhase % 1;
        let diamondOffset;
        if (diamondSegment === 0) diamondOffset = diamondT;
        else if (diamondSegment === 1) diamondOffset = 1 - diamondT;
        else if (diamondSegment === 2) diamondOffset = -diamondT;
        else diamondOffset = diamondT - 1;
        x = baseX + diamondOffset * amplitude * 0.7;
        break;
      
      case 'helix':
        // Helix/DNA spiral pattern
        const helixFreq = progress * Math.PI * 4;
        const helixAmp = amplitude * 0.5 * (0.5 + Math.sin(progress * Math.PI) * 0.5);
        x = baseX + Math.sin(helixFreq) * helixAmp;
        break;
      
      case 'forestVine':
        // Organic vine-like meandering path for Dark Forest
        const vineMain = Math.sin(progress * Math.PI * 2) * amplitude * 0.4;
        const vineDetail = Math.sin(progress * Math.PI * 7) * amplitude * 0.15;
        const vineWobble = Math.cos(progress * Math.PI * 11) * amplitude * 0.08;
        x = baseX + vineMain + vineDetail + vineWobble;
        break;
      
      case 'darkSpiral':
        // Dark ominous spiral for final Dark Forest section
        const darkPhase = progress * Math.PI * 5;
        const darkRadius = amplitude * (0.3 + progress * 0.4);
        const darkTwist = Math.sin(progress * Math.PI * 8) * amplitude * 0.1;
        x = baseX + Math.sin(darkPhase) * darkRadius * 0.5 + darkTwist;
        break;
      
      // === OCEAN DEPTHS PATTERNS ===
      case 'oceanWave':
        // Flowing ocean wave pattern with currents
        const oceanMain = Math.sin(progress * Math.PI * 2.5) * amplitude * 0.6;
        const oceanRipple = Math.sin(progress * Math.PI * 8) * amplitude * 0.12;
        const oceanDrift = Math.cos(progress * Math.PI * 1.5) * amplitude * 0.2;
        x = baseX + oceanMain + oceanRipple + oceanDrift;
        break;
      
      case 'deepDive':
        // Deep diving path that curves down then up
        const divePhase = progress * Math.PI * 3;
        const diveDepth = Math.sin(progress * Math.PI) * amplitude * 0.3;
        const diveCurrent = Math.sin(divePhase) * amplitude * 0.5;
        x = baseX + diveCurrent + diveDepth;
        break;
      
      // === FROZEN PEAKS PATTERNS ===
      case 'mountainClimb':
        // Steep mountain switchback path
        const switchbacks = 4;
        const climbPhase = (progress * switchbacks) % 1;
        const climbDir = Math.floor(progress * switchbacks) % 2 === 0 ? 1 : -1;
        const climbCurve = Math.sin(climbPhase * Math.PI) * climbDir;
        x = baseX + climbCurve * amplitude * 0.7;
        break;
      
      case 'glacierPath':
        // Icy glacier path with sharp turns
        const glacierMain = Math.sin(progress * Math.PI * 3) * amplitude * 0.5;
        const glacierCrack = Math.sin(progress * Math.PI * 9) * amplitude * 0.15 * (1 - progress);
        x = baseX + glacierMain + glacierCrack;
        break;
      
      // === ANCIENT RUINS PATTERNS ===
      case 'pyramidSteps':
        // Stepped pyramid path
        const steps = 5;
        const stepPhase = (progress * steps) % 1;
        const stepLevel = Math.floor(progress * steps);
        const stepOffset = (stepLevel % 2 === 0 ? 1 : -1) * (0.5 - Math.abs(stepPhase - 0.5));
        x = baseX + stepOffset * amplitude * 0.8;
        break;
      
      case 'templeSpiral':
        // Ancient temple spiral leading to the center
        const templePhase = progress * Math.PI * 4;
        const templeRadius = amplitude * 0.6 * (1 - progress * 0.3);
        const templeDrift = Math.sin(progress * Math.PI * 2) * amplitude * 0.15;
        x = baseX + Math.sin(templePhase) * templeRadius * 0.5 + templeDrift;
        break;
      
      // === NEW WORLD PATTERNS (201-400) ===
      case 'volcanicRise':
        const volcRise = Math.sin(progress * Math.PI * 2.5) * amplitude * 0.6;
        const volcBubble = Math.sin(progress * Math.PI * 8) * amplitude * 0.1;
        x = baseX + volcRise + volcBubble;
        break;
      
      case 'magmaFlow':
        const magmaWave = Math.sin(progress * Math.PI * 3) * amplitude * 0.55;
        const magmaRipple = Math.cos(progress * Math.PI * 7) * amplitude * 0.12;
        x = baseX + magmaWave + magmaRipple;
        break;
      
      case 'moonlightPath':
        const moonCurve = Math.sin(progress * Math.PI * 2) * amplitude * 0.5;
        const moonGlow = Math.sin(progress * Math.PI * 5) * amplitude * 0.15;
        x = baseX + moonCurve + moonGlow;
        break;
      
      case 'lunarOrbit':
        const lunarPhase = progress * Math.PI * 3.5;
        const lunarRadius = amplitude * 0.5 * (1 + Math.sin(progress * Math.PI) * 0.3);
        x = baseX + Math.sin(lunarPhase) * lunarRadius * 0.6;
        break;
      
      case 'alchemySpiral':
        const alchPhase = progress * Math.PI * 4;
        const alchRadius = amplitude * 0.4 * (1 + progress * 0.4);
        x = baseX + Math.sin(alchPhase) * alchRadius;
        break;
      
      case 'potionBubble':
        const bubbleWave = Math.sin(progress * Math.PI * 3) * amplitude * 0.45;
        const bubblePop = Math.sin(progress * Math.PI * 10) * amplitude * 0.1 * (1 - progress);
        x = baseX + bubbleWave + bubblePop;
        break;
      
      case 'circusTent':
        const tentPeaks = 3;
        const tentPhase = (progress * tentPeaks) % 1;
        const tentDir = Math.floor(progress * tentPeaks) % 2 === 0 ? 1 : -1;
        x = baseX + Math.sin(tentPhase * Math.PI) * amplitude * 0.6 * tentDir;
        break;
      
      case 'carnivalLoop':
        const loopPhase = progress * Math.PI * 4;
        const loopRadius = amplitude * 0.5;
        x = baseX + Math.sin(loopPhase) * loopRadius * Math.sin(progress * Math.PI);
        break;
      
      case 'shipwreckPath':
        const shipRock = Math.sin(progress * Math.PI * 2.5) * amplitude * 0.55;
        const waveMotion = Math.sin(progress * Math.PI * 7) * amplitude * 0.12;
        x = baseX + shipRock + waveMotion;
        break;
      
      case 'treasureTrail':
        const treasureZig = 4;
        const treasurePhase = (progress * treasureZig) % 1;
        const treasureDir = Math.floor(progress * treasureZig) % 2 === 0 ? 1 : -1;
        x = baseX + (treasurePhase * 2 - 1) * amplitude * 0.5 * treasureDir;
        break;
      
      case 'blossomWind':
        const blossomDrift = Math.sin(progress * Math.PI * 2) * amplitude * 0.5;
        const petalFloat = Math.sin(progress * Math.PI * 6) * amplitude * 0.15;
        x = baseX + blossomDrift + petalFloat;
        break;
      
      case 'templeBridge':
        const bridgeCurve = Math.sin(progress * Math.PI) * amplitude * 0.4;
        const bridgeSway = Math.sin(progress * Math.PI * 4) * amplitude * 0.2;
        x = baseX + bridgeCurve + bridgeSway;
        break;
      
      case 'lightningBolt':
        const boltZigs = 5;
        const boltPhase = (progress * boltZigs) % 1;
        const boltDir = Math.floor(progress * boltZigs) % 2 === 0 ? 1 : -1;
        x = baseX + (boltPhase < 0.5 ? boltPhase * 2 : 2 - boltPhase * 2) * amplitude * 0.7 * boltDir;
        break;
      
      case 'thunderCloud':
        const cloudDrift = Math.sin(progress * Math.PI * 2) * amplitude * 0.45;
        const rumble = Math.sin(progress * Math.PI * 8) * amplitude * 0.15;
        x = baseX + cloudDrift + rumble;
        break;
      
      case 'mushroomPath':
        const shroomBounce = Math.sin(progress * Math.PI * 3) * amplitude * 0.5;
        const sporeFloat = Math.cos(progress * Math.PI * 7) * amplitude * 0.12;
        x = baseX + shroomBounce + sporeFloat;
        break;
      
      case 'cavernDepths':
        const cavernWind = Math.sin(progress * Math.PI * 2.5) * amplitude * 0.55;
        const echoDrift = Math.sin(progress * Math.PI * 6) * amplitude * 0.1;
        x = baseX + cavernWind + echoDrift;
        break;
      
      case 'casinoChip':
        const chipSpin = progress * Math.PI * 3;
        const chipWobble = amplitude * 0.5 * Math.sin(progress * Math.PI);
        x = baseX + Math.sin(chipSpin) * chipWobble;
        break;
      
      case 'luckyStreak':
        const streakWave = Math.sin(progress * Math.PI * 4) * amplitude * 0.4;
        const luckBounce = Math.sin(progress * Math.PI * 9) * amplitude * 0.15;
        x = baseX + streakWave + luckBounce;
        break;
      
      case 'cosmicWarp':
        const warpPhase = progress * Math.PI * 5;
        const warpRadius = amplitude * 0.5 * (0.5 + Math.sin(progress * Math.PI) * 0.5);
        x = baseX + Math.sin(warpPhase) * warpRadius;
        break;
      
      case 'nebulaSpiral':
        const nebPhase = progress * Math.PI * 4;
        const nebRadius = amplitude * 0.6 * (1 - progress * 0.2);
        const nebDust = Math.sin(progress * Math.PI * 8) * amplitude * 0.08;
        x = baseX + Math.sin(nebPhase) * nebRadius * 0.5 + nebDust;
        break;
      
      // === ROYAL CITADEL (VIP WORLD) PATTERNS ===
      case 'throneAscent':
        // Majestic ascending path with royal curves
        const throneWave = Math.sin(progress * Math.PI * 1.5) * amplitude * 0.4;
        const royalCurve = Math.sin(progress * Math.PI * 4) * amplitude * 0.2;
        x = baseX + throneWave + royalCurve;
        break;
      
      case 'royalCorridor':
        // Grand corridor with alternating alcoves
        const corridorSweep = Math.sin(progress * Math.PI * 2) * amplitude * 0.5;
        const alcoveDetail = Math.sin(progress * Math.PI * 6) * amplitude * 0.15;
        x = baseX + corridorSweep + alcoveDetail;
        break;
      
      case 'crownPath':
        // Crown-shaped path with peaks like crown points
        const crownPeaks = 4;
        const crownPhase = (progress * crownPeaks) % 1;
        const crownPoint = Math.sin(crownPhase * Math.PI) * amplitude * 0.6;
        const crownBase = Math.sin(progress * Math.PI) * amplitude * 0.2;
        x = baseX + crownPoint + crownBase;
        break;
      
      case 'throneSpiral':
        // Spiral approach to the throne
        const throneSpPhase = progress * Math.PI * 3.5;
        const throneSpRadius = amplitude * 0.55 * (1 - progress * 0.3);
        x = baseX + Math.sin(throneSpPhase) * throneSpRadius;
        break;
      
      case 'royalFinale':
        // Grand finale leading to the throne
        const finaleWave = Math.sin(progress * Math.PI * 2) * amplitude * 0.35;
        const finaleFlourish = Math.sin(progress * Math.PI * 5) * amplitude * 0.2 * (1 - progress);
        const finaleGrand = Math.sin(progress * Math.PI) * amplitude * 0.15;
        x = baseX + finaleWave + finaleFlourish + finaleGrand;
        break;
      
      // === CRYSTAL PARADISE (MEMBER WORLD) PATTERNS ===
      case 'diamondRise':
        // Rising diamond facets pattern
        const diamFacets = 5;
        const diamPhase = (progress * diamFacets) % 1;
        const diamDir = Math.floor(progress * diamFacets) % 2 === 0 ? 1 : -1;
        const diamCut = Math.sin(diamPhase * Math.PI) * amplitude * 0.5 * diamDir;
        const diamShimmer = Math.sin(progress * Math.PI * 8) * amplitude * 0.1;
        x = baseX + diamCut + diamShimmer;
        break;
      
      case 'crystalCascade':
        // Cascading crystal waterfall pattern
        const cascadeMain = Math.sin(progress * Math.PI * 2.5) * amplitude * 0.55;
        const cascadeDrip = Math.sin(progress * Math.PI * 7) * amplitude * 0.15;
        const cascadeSparkle = Math.cos(progress * Math.PI * 12) * amplitude * 0.05;
        x = baseX + cascadeMain + cascadeDrip + cascadeSparkle;
        break;
      
      case 'prismPath':
        // Prismatic light refraction pattern
        const prismAngle = progress * Math.PI * 3;
        const prismBend = Math.sin(prismAngle) * amplitude * 0.5;
        const lightSplit = Math.sin(progress * Math.PI * 6) * amplitude * 0.2 * Math.sin(progress * Math.PI);
        x = baseX + prismBend + lightSplit;
        break;
      
      case 'skyPalaceSpiral':
        // Ethereal spiral ascending to the sky palace
        const palacePhase = progress * Math.PI * 4;
        const palaceRadius = amplitude * 0.5 * (0.6 + Math.sin(progress * Math.PI) * 0.4);
        const cloudFloat = Math.sin(progress * Math.PI * 2) * amplitude * 0.1;
        x = baseX + Math.sin(palacePhase) * palaceRadius + cloudFloat;
        break;
      
      case 'celestialAscent':
        // Final celestial ascent with heavenly curves
        const celestialWave = Math.sin(progress * Math.PI * 1.5) * amplitude * 0.4;
        const starPath = Math.sin(progress * Math.PI * 4) * amplitude * 0.25;
        const heavenGlow = Math.sin(progress * Math.PI * 8) * amplitude * 0.1 * (1 - progress);
        x = baseX + celestialWave + starPath + heavenGlow;
        break;
      
      default:
        // Fallback to simple curve
        x = baseX + Math.sin(progress * Math.PI) * amplitude * 0.5;
    }
    
    // Clamp X to stay within bounds (15% - 85%)
    x = Math.max(15, Math.min(85, x));
    
    return { x, y };
  };

  // Calculate path positions for levels
  const getPathPosition = (index, total, pageNum = currentPage) => {
    const progress = index / (total - 1 || 1);
    return calculatePathPoint(progress, pageNum);
  };

  // Generate SVG path - diverse shapes that connect between pages
  const generatePathD = () => {
    const points = [];
    const steps = 60; // More steps for smoother curves
    
    for (let i = 0; i <= steps; i++) {
      const progress = i / steps;
      const point = calculatePathPoint(progress, currentPage);
      points.push(point);
    }
    
    // Create smooth bezier curve path
    let d = `M ${points[0].x} ${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
      // Use quadratic bezier for smoother curves
      const prev = points[i - 1];
      const curr = points[i];
      const midX = (prev.x + curr.x) / 2;
      const midY = (prev.y + curr.y) / 2;
      d += ` Q ${prev.x} ${prev.y} ${midX} ${midY}`;
    }
    
    // Final point
    const last = points[points.length - 1];
    d += ` L ${last.x} ${last.y}`;
    
    return d;
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handlePlayLevel = (levelId) => {
    if (!canPlay) return;
    const isUnlocked = levelId === 1 || completedLevels.includes(levelId - 1);
    if (isUnlocked) {
      navigate(`/play/${levelId}`);
    }
  };

  const handleTileClick = (lvl) => {
    const isUnlocked = lvl.id === 1 || completedLevels.includes(lvl.id - 1);
    if (isUnlocked && canPlay) {
      setSelectedLevel(lvl);
    }
  };

  const goToPage = (direction) => {
    // Trigger page transition animation
    setPageTransition(direction > 0 ? 'page-slide-left' : 'page-slide-right');
    
    // Clear animation after it completes
    setTimeout(() => setPageTransition(''), 400);
    
    setCurrentPage(prev => {
      const newPage = prev + direction;
      // Clear traveled page when user manually navigates with arrows
      sessionStorage.removeItem('fruitCrush_traveledPage');
      setHasManuallyNavigated(false);
      return Math.max(0, Math.min(totalPages - 1, newPage));
    });
  };

  // Auto-navigate to current progress page (only on initial load, not after manual travel)
  useEffect(() => {
    // Check if user has traveled to a specific world this session
    const traveledPage = sessionStorage.getItem('fruitCrush_traveledPage');
    if (traveledPage !== null) {
      setCurrentPage(parseInt(traveledPage, 10));
      return;
    }
    
    // On initial load, navigate to current progress
    const currentLevel = completedLevels.length + 1;
    const targetPage = Math.floor((currentLevel - 1) / LEVELS_PER_PAGE);
    setCurrentPage(Math.min(targetPage, totalPages - 1));
  }, []); // Only run on initial mount

  const prestigeStars = prestige > 0 ? '⭐'.repeat(Math.min(prestige, 5)) + (prestige > 5 ? `+${prestige - 5}` : '') : null;

  // Get world name for TopHUD
  const getWorldName = () => {
    const names = {
      1: 'Fruit Garden',
      2: 'Ice World',
      3: 'Chain Realm',
      4: 'Chocolate Factory',
      5: 'Honey Hive',
      6: 'Lava World',
      7: 'Point World',
      8: 'Dark Forest',
      9: 'Ocean Depths',
      10: 'Frozen Peaks',
      11: 'Ancient Ruins',
      12: 'Volcanic Core',
      13: 'Moonlight Garden',
      14: 'Alchemist\'s Lab',
      15: 'Circus Carnival',
      16: 'Pirate Shipwreck',
      17: 'Cherry Blossom',
      18: 'Storm Kingdom',
      19: 'Mushroom Cavern',
      20: 'Casino Royale',
      21: 'Cosmic Nebula',
      99: 'Crystal Paradise',
      100: 'Royal Citadel'
    };
    return names[currentWorld] || 'Fruit Garden';
  };

  const getWorldEmoji = () => {
    const emojis = {
      1: '🌸', 2: '🧊', 3: '⛓️', 4: '🍫', 5: '🍯', 6: '🌋',
      7: '🎯', 8: '🦉', 9: '🌊', 10: '❄️', 11: '🏛️',
      12: '🔥', 13: '🌙', 14: '⚗️', 15: '🎪', 16: '🏴‍☠️',
      17: '🌸', 18: '⚡', 19: '🍄', 20: '🎰', 21: '🌌',
      99: '💎', 100: '👑'
    };
    return emojis[currentWorld] || '🌸';
  };

  return (
    <>
      {/* Tutorial Overlay for new users */}
      {showTutorial && (
        <TutorialOverlay onComplete={() => { setShowTutorial(false); setForceTutorial(false); }} forceShow={forceTutorial} />
      )}

      {/* World Lore popup on first visit */}
      {showWorldLore && (
        <div 
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowWorldLore(null)}
          data-testid="world-lore-modal"
        >
          <div 
            className="bg-gradient-to-b from-slate-800/95 to-slate-900/95 border border-amber-500/30 rounded-2xl max-w-sm w-full p-6 text-center space-y-4 shadow-2xl shadow-amber-500/10"
            onClick={e => e.stopPropagation()}
            style={{ animation: 'worldLoreIn 0.5s ease-out' }}
          >
            <div className="text-5xl" style={{ animation: 'worldLoreBounce 0.8s ease-out' }}>
              {getWorldEmoji()}
            </div>
            <h2 className="font-display text-xl font-bold text-amber-300 tracking-wide">
              {showWorldLore.name?.replace(/[^a-zA-Z\s']/g, '').trim()}
            </h2>
            <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto" />
            <p className="text-slate-300 text-sm leading-relaxed font-body">
              {showWorldLore.lore}
            </p>
            <button
              onClick={() => setShowWorldLore(null)}
              className="mt-2 px-6 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 rounded-lg text-amber-300 text-sm font-bold transition-colors"
              data-testid="world-lore-dismiss"
            >
              Begin Adventure
            </button>
          </div>
          <style>{`
            @keyframes worldLoreIn {
              0% { opacity: 0; transform: translateY(30px) scale(0.95); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
            @keyframes worldLoreBounce {
              0% { transform: scale(0.3); opacity: 0; }
              50% { transform: scale(1.2); }
              100% { transform: scale(1); opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowProgressModal(false)}
        >
          <Card 
            className="bg-slate-900 border-slate-700 w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Trophy className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">My Progress</h2>
                    <p className="text-white/70 text-sm">{user?.player_name || 'Player'}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowProgressModal(false)}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            <CardContent className="p-4 space-y-4">
              {/* Level Progress */}
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-sm">Levels Completed</span>
                  <span className="text-white font-bold">{completedLevels.length} / {LEVELS.length}</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all"
                    style={{ width: `${(completedLevels.length / LEVELS.length) * 100}%` }}
                  />
                </div>
                <p className="text-slate-500 text-xs mt-2">
                  Next level: {completedLevels.length + 1 <= LEVELS.length ? `Level ${completedLevels.length + 1}` : 'All Complete!'}
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-800/50 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <Star className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold">{totalStars.toLocaleString()}</p>
                    <p className="text-slate-400 text-xs">Total Stars</p>
                  </div>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold">{totalXP.toLocaleString()}</p>
                    <p className="text-slate-400 text-xs">Total XP</p>
                  </div>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold">Level {level}</p>
                    <p className="text-slate-400 text-xs">Player Level</p>
                  </div>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold">{prestige}</p>
                    <p className="text-slate-400 text-xs">Prestige</p>
                  </div>
                </div>
              </div>

              {/* Membership Status */}
              {membershipStatus?.is_premium_member && (
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-3 flex items-center gap-3">
                  <Crown className="w-6 h-6 text-amber-400" />
                  <div>
                    <p className="text-amber-400 font-medium">Premium Member</p>
                    <p className="text-slate-400 text-xs">+500 HP Bonus Active</p>
                  </div>
                </div>
              )}

              {/* Go to Current Level Button */}
              <Button 
                onClick={() => {
                  setShowProgressModal(false);
                  // Navigate to current level
                  sessionStorage.removeItem('fruitCrush_traveledPage');
                  setHasManuallyNavigated(false);
                  const progressLevel = completedLevels.length + 1;
                  const progressPage = Math.floor((progressLevel - 1) / LEVELS_PER_PAGE);
                  setCurrentPage(Math.min(progressPage, totalPages - 1));
                  toast.success(`Navigated to Level ${Math.min(progressLevel, LEVELS.length)}`);
                }}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                <Target className="w-4 h-4 mr-2" />
                Go to Current Level
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Profile Sidebar - New design */}
      <ProfileSidebar
        isOpen={showProfileSidebar}
        onClose={() => setShowProfileSidebar(false)}
        onMenuSelect={(id) => {
          setShowProfileSidebar(false);
          handleMenuSelect(id);
        }}
        currentStars={totalStars}
        canSpin={canSpinToday()}
        challengeCount={challengeCounts.canClaim}
      />

      {/* Social Menu Popup */}
      <SocialMenu
        isOpen={showSocialMenu}
        onClose={() => setShowSocialMenu(false)}
        onSelectOption={handleSocialOption}
      />

      {/* Old Sidebar Menu - Keep for additional menu items */}
      <SidebarMenu
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        onMenuSelect={handleMenuSelect}
        challengeCount={challengeCounts.canClaim}
        currentStars={totalStars}
        canSpin={canSpinToday()}
      />

      {/* Top HUD - Simplified header */}
      <TopHUD
        worldName={getWorldName()}
        worldEmoji={getWorldEmoji()}
        hp={hp}
        maxHP={maxHP}
        coins={totalCoins}
        gems={totalStars}
        onHealthClick={() => setShowShop(true)}
        onCoinsClick={() => { setShopInitialTab('consumables'); setShowShop(true); }}
        onGemsClick={() => { setShopInitialTab('consumables'); setShowShop(true); }}
      />

      {/* Hamburger Menu - Bottom Left */}
      <HamburgerMenu
        onMenuSelect={handleMenuSelect}
        hasNotification={challengeCounts.canClaim > 0}
        isPremiumMember={membershipStatus?.is_premium_member || false}
        level={level}
        currentXP={currentXP}
        neededXP={neededXP}
        prestige={prestige}
      />

      {/* Floating Chat Button - Bottom Right */}
      <button
        onClick={() => setShowChat(true)}
        className="fixed right-4 bottom-4 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 flex items-center justify-center shadow-lg transition-all duration-200 active:scale-95 border border-white/20"
        data-testid="chat-fab"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </button>

      {/* Floating PvP Button - Bottom Right above chat */}
      <button
        onClick={() => setShowPvP(true)}
        className="fixed right-4 bottom-20 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 flex items-center justify-center shadow-lg transition-all duration-200 active:scale-95 border border-white/20 animate-pulse"
        data-testid="pvp-fab"
      >
        <Swords className="w-6 h-6 text-white" />
      </button>
      
      <div className="min-h-screen overflow-hidden pt-16 pb-20" style={{ background: worldTheme?.background || 'linear-gradient(to bottom, #87CEEB 0%, #90EE90 60%, #228B22 100%)' }}>
      
      {/* Standard container for 2D map */}
      <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3 max-w-lg">

        {/* HP Regen Info - Shows real-time regen rate */}
        {hp < maxHP && (
          <div className="flex items-center justify-center gap-1 mb-2 text-xs text-white/80 bg-black/20 rounded-full px-3 py-1 mx-auto w-fit">
            <Heart className="w-3 h-3 text-green-400 animate-pulse" />
            <span>{getRegenInfo()}</span>
          </div>
        )}

        {/* Limited-Time Event Banners */}
        {activeEvents.length > 0 && (
          <div className="space-y-1.5 mb-2" data-testid="event-banners">
            {activeEvents.map(event => (
              <div key={event.id} className="bg-gradient-to-r from-amber-600/80 via-orange-500/80 to-amber-600/80 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-2 border border-amber-400/30 shadow-lg">
                <span className="text-xl animate-bounce" style={{ animationDuration: '2s' }}>{event.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-xs leading-tight">{event.name}</p>
                  <p className="text-white/70 text-[10px] leading-tight truncate">{event.description}</p>
                </div>
                {event.multiplier > 1 && (
                  <span className="text-amber-200 font-black text-sm">{event.multiplier}X</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* No HP Warning */}
        {!canPlay && (
          <div className="bg-red-500/80 text-white rounded-lg p-3 mb-2 text-center text-sm animate-pulse border border-red-400">
            <Heart className="w-5 h-5 inline mr-2 animate-bounce" />
            <span className="font-bold">Not enough HP!</span> {getRegenInfo()}
          </div>
        )}
        
        {/* Original 2D Game Map */}
        <div className="relative map-3d-container">
          {/* Page up button */}
          <Button
            onClick={() => goToPage(1)}
            disabled={currentPage >= totalPages - 1}
            size="sm"
            data-testid="page-up-btn"
            className="absolute -top-2 left-1/2 -translate-x-1/2 z-20 bg-white/90 hover:bg-white rounded-full shadow-lg btn-hover-effect"
          >
            <ChevronUp className="w-5 h-5" />
          </Button>

          {/* Map container with 3D perspective and page transition */}
          <div 
            ref={mapRef}
            className={`relative h-[450px] overflow-hidden rounded-2xl border-4 shadow-2xl mt-4 mb-4 transition-all duration-500 ${pageTransition}`}
            style={{
              borderColor: currentWorld === 2 ? '#67E8F9' : 
                          currentWorld === 3 ? '#64748B' :
                          currentWorld === 4 ? '#D97706' :
                          currentWorld === 5 ? '#F59E0B' :
                          currentWorld === 6 ? '#A855F7' :
                          currentWorld === 7 ? '#A855F7' :
                          currentWorld === 8 ? '#22C55E' :
                          currentWorld === 9 ? '#0EA5E9' :
                          currentWorld === 10 ? '#38BDF8' :
                          currentWorld === 11 ? '#D97706' : '#92400E',
              boxShadow: `
                inset 0 0 100px rgba(0,0,0,0.2),
                0 10px 40px rgba(0,0,0,0.4),
                0 0 0 2px rgba(255,255,255,0.1)
              `,
            }}
          >
            {/* NEW: Parallax Background with world-specific themes */}
            <ParallaxBackground 
              key={`parallax-${currentWorld}`}
              worldId={currentWorld} 
              scrollY={0}
              height={450}
            />
            
            {/* Ground texture overlay */}
            <div className="absolute inset-0 ground-texture pointer-events-none opacity-30 z-10" />
            
            {/* Cloud shadows drifting */}
            <div className="cloud-shadow w-40 h-20 top-[20%] z-10" style={{ animationDelay: '0s' }} />
            <div className="cloud-shadow w-32 h-16 top-[50%] z-10" style={{ animationDelay: '7s' }} />
            <div className="cloud-shadow w-48 h-24 top-[75%] z-10" style={{ animationDelay: '14s' }} />
            
            {/* Special world decorations (kept for level-specific elements) */}
            <div className="absolute inset-0 pointer-events-none z-15">
              {/* Castle at the end of the road - ONLY on the final page (level 100) */}
              {currentPage === totalPages - 1 && (
                <div 
                  className="absolute text-5xl drop-shadow-lg animate-pulse z-20"
                  style={{
                    // Position at the end of the path (where level 100 is)
                    left: '25%',
                    top: '2%',
                    transform: 'translateX(-50%)'
                  }}
                >🏰</div>
              )}
              
              {/* World indicator removed - world name is already shown in header */}
              
              {/* World-specific effects are now handled by ParallaxBackground component */}

              {/* Crystal Paradise (World 99) - "Diamond Sky Palace" - Realistic ethereal sky effects */}
              {currentWorld === 99 && (
                <>
                  {/* Members Badge */}
                  <div className="absolute top-[2%] left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-500/90 via-purple-500/90 to-pink-500/90 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg z-30 backdrop-blur-sm border border-white/30">
                    MEMBERS ONLY
                  </div>
                  
                  {/* Soft ethereal clouds */}
                  <div className="absolute top-[12%] left-[3%] w-24 h-12 bg-white/50 rounded-full blur-xl animate-cloud-drift" />
                  <div className="absolute top-[22%] right-[5%] w-20 h-10 bg-white/40 rounded-full blur-lg animate-cloud-drift" style={{ animationDelay: '3s' }} />
                  <div className="absolute top-[45%] left-[5%] w-28 h-14 bg-white/35 rounded-full blur-xl animate-cloud-drift" style={{ animationDelay: '6s' }} />
                  <div className="absolute top-[60%] right-[8%] w-18 h-9 bg-white/30 rounded-full blur-lg animate-cloud-drift" style={{ animationDelay: '9s' }} />
                  
                  {/* Prismatic light beams */}
                  <div className="absolute top-0 left-[18%] w-px h-full bg-gradient-to-b from-rose-300/40 via-amber-200/20 to-transparent transform -skew-x-6" />
                  <div className="absolute top-0 left-[32%] w-0.5 h-full bg-gradient-to-b from-cyan-300/30 via-emerald-200/15 to-transparent transform skew-x-3" />
                  <div className="absolute top-0 right-[28%] w-px h-full bg-gradient-to-b from-violet-300/35 via-blue-200/20 to-transparent transform -skew-x-3" />
                  <div className="absolute top-0 right-[12%] w-0.5 h-full bg-gradient-to-b from-fuchsia-300/30 via-pink-200/15 to-transparent transform skew-x-6" />
                  
                  {/* Crystal sparkle particles */}
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={`sparkle-${i}`}
                      className="absolute w-1.5 h-1.5 bg-white rounded-full animate-crystal-sparkle"
                      style={{
                        left: `${12 + (i * 8) % 76}%`,
                        top: `${15 + (i * 12) % 65}%`,
                        animationDelay: `${i * 0.35}s`,
                        boxShadow: '0 0 8px 3px rgba(255,255,255,0.7), 0 0 16px 6px rgba(147,197,253,0.5)'
                      }}
                    />
                  ))}
                  
                  {/* Floating crystal shards */}
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={`crystal-${i}`}
                      className="absolute animate-crystal-rise"
                      style={{
                        left: `${8 + i * 15}%`,
                        bottom: '-15px',
                        animationDelay: `${i * 1.8}s`,
                        animationDuration: '10s'
                      }}
                    >
                      <div 
                        className="w-2.5 h-4 bg-gradient-to-t from-cyan-400/70 to-white/90 shadow-lg"
                        style={{ 
                          clipPath: 'polygon(50% 0%, 100% 40%, 80% 100%, 20% 100%, 0% 40%)',
                          boxShadow: '0 0 10px rgba(147,197,253,0.6)'
                        }}
                      />
                    </div>
                  ))}
                  
                  {/* Soft rainbow arc */}
                  <div className="absolute top-[8%] left-[15%] w-48 h-24 opacity-25 pointer-events-none"
                    style={{
                      background: 'conic-gradient(from 180deg, transparent 0%, rgba(239,68,68,0.4) 14%, rgba(249,115,22,0.4) 28%, rgba(234,179,8,0.4) 42%, rgba(34,197,94,0.4) 57%, rgba(59,130,246,0.4) 71%, rgba(139,92,246,0.4) 85%, transparent 100%)',
                      borderRadius: '100% 100% 0 0',
                      transform: 'scaleY(0.4)'
                    }}
                  />
                  
                  {/* Ambient glow orbs */}
                  <div className="absolute top-[25%] left-[8%] w-16 h-16 bg-cyan-300/25 rounded-full blur-2xl animate-pulse" />
                  <div className="absolute top-[50%] right-[10%] w-20 h-20 bg-violet-300/25 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1.2s' }} />
                  <div className="absolute top-[70%] left-[15%] w-14 h-14 bg-pink-300/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2.4s' }} />
                  
                  {/* Celestial shimmer overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/8 via-cyan-200/3 to-purple-300/8 pointer-events-none" />
                </>
              )}

              {/* Royal Citadel (World 100) - "Throne Room of Ages" - Realistic medieval throne room */}
              {currentWorld === 100 && (
                <>
                  {/* VIP Elite Badge */}
                  <div className="absolute top-[2%] left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-700/95 via-yellow-500/95 to-amber-700/95 text-amber-950 px-4 py-1.5 rounded-full text-xs font-bold shadow-lg z-30 backdrop-blur-sm border border-amber-300/50">
                    THRONE ROOM • 12×12
                  </div>
                  
                  {/* Stone wall texture */}
                  <div className="absolute inset-0 opacity-15 pointer-events-none"
                    style={{
                      backgroundImage: `repeating-linear-gradient(
                        90deg,
                        transparent 0px,
                        transparent 35px,
                        rgba(0,0,0,0.08) 35px,
                        rgba(0,0,0,0.08) 37px
                      ),
                      repeating-linear-gradient(
                        0deg,
                        transparent 0px,
                        transparent 25px,
                        rgba(0,0,0,0.06) 25px,
                        rgba(0,0,0,0.06) 27px
                      )`
                    }}
                  />
                  
                  {/* Torches with realistic flame glow */}
                  <div className="absolute top-[12%] left-[3%]">
                    <div className="w-2 h-10 bg-gradient-to-t from-stone-800 to-stone-600 rounded-t-sm" />
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-6 h-8 bg-gradient-to-t from-amber-500/90 via-orange-400/70 to-yellow-300/50 rounded-full blur-[2px] animate-torch-flicker" />
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-3 h-4 bg-gradient-to-t from-orange-400 to-yellow-100 rounded-full animate-torch-flicker" style={{ animationDelay: '0.05s' }} />
                  </div>
                  <div className="absolute top-[12%] right-[3%]">
                    <div className="w-2 h-10 bg-gradient-to-t from-stone-800 to-stone-600 rounded-t-sm" />
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-6 h-8 bg-gradient-to-t from-amber-500/90 via-orange-400/70 to-yellow-300/50 rounded-full blur-[2px] animate-torch-flicker" style={{ animationDelay: '0.2s' }} />
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-3 h-4 bg-gradient-to-t from-orange-400 to-yellow-100 rounded-full animate-torch-flicker" style={{ animationDelay: '0.25s' }} />
                  </div>
                  <div className="absolute top-[42%] left-[3%]">
                    <div className="w-1.5 h-8 bg-gradient-to-t from-stone-800 to-stone-600 rounded-t-sm" />
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-5 h-6 bg-gradient-to-t from-amber-500/80 via-orange-400/60 to-yellow-300/40 rounded-full blur-[2px] animate-torch-flicker" style={{ animationDelay: '0.4s' }} />
                  </div>
                  <div className="absolute top-[42%] right-[3%]">
                    <div className="w-1.5 h-8 bg-gradient-to-t from-stone-800 to-stone-600 rounded-t-sm" />
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-5 h-6 bg-gradient-to-t from-amber-500/80 via-orange-400/60 to-yellow-300/40 rounded-full blur-[2px] animate-torch-flicker" style={{ animationDelay: '0.6s' }} />
                  </div>
                  
                  {/* Stone pillars */}
                  <div className="absolute top-[22%] left-[6%] w-3 h-40 bg-gradient-to-r from-stone-700/60 via-stone-500/50 to-stone-700/60 rounded-t" />
                  <div className="absolute top-[22%] right-[6%] w-3 h-40 bg-gradient-to-r from-stone-700/60 via-stone-500/50 to-stone-700/60 rounded-t" />
                  
                  {/* Stained glass windows */}
                  <div className="absolute top-[8%] left-[1.5%] w-5 h-14 rounded-t-full overflow-hidden opacity-70 border border-stone-600/50">
                    <div className="w-full h-full bg-gradient-to-b from-red-500/60 via-amber-400/50 to-blue-500/60" />
                  </div>
                  <div className="absolute top-[8%] right-[1.5%] w-5 h-14 rounded-t-full overflow-hidden opacity-70 border border-stone-600/50">
                    <div className="w-full h-full bg-gradient-to-b from-purple-500/60 via-emerald-400/50 to-amber-500/60" />
                  </div>
                  
                  {/* Royal banners */}
                  <div className="absolute top-[10%] left-[9%] w-2.5 h-12 bg-gradient-to-b from-red-800 via-red-700 to-red-900 rounded-b-sm opacity-80 shadow-md" />
                  <div className="absolute top-[10%] right-[9%] w-2.5 h-12 bg-gradient-to-b from-purple-800 via-purple-700 to-purple-900 rounded-b-sm opacity-80 shadow-md" />
                  
                  {/* Gold dust particles */}
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={`dust-${i}`}
                      className="absolute w-1 h-1 bg-amber-300/70 rounded-full animate-gold-dust"
                      style={{
                        left: `${10 + (i * 7) % 80}%`,
                        top: `${12 + (i * 8) % 72}%`,
                        animationDelay: `${i * 0.35}s`,
                        boxShadow: '0 0 5px 2px rgba(251,191,36,0.6)'
                      }}
                    />
                  ))}
                  
                  {/* Warm ambient light */}
                  <div className="absolute top-[18%] left-[18%] w-24 h-24 bg-amber-400/12 rounded-full blur-3xl" />
                  <div className="absolute top-[38%] right-[20%] w-20 h-20 bg-orange-400/12 rounded-full blur-3xl" />
                  <div className="absolute bottom-[25%] left-[28%] w-28 h-28 bg-yellow-400/10 rounded-full blur-3xl" />
                  
                  {/* Crown emblem glow */}
                  <div className="absolute top-[4%] left-1/2 -translate-x-1/2 w-12 h-12 bg-amber-400/25 rounded-full blur-xl animate-pulse" />
                  
                  {/* Golden shimmer overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-amber-900/8 via-yellow-200/3 to-amber-800/10 pointer-events-none" />
                </>
              )}
            </div>

            {/* The winding path (SVG) - 3D styled with shimmer */}
            <svg 
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {/* Path deep shadow */}
              <path
                d={generatePathD()}
                fill="none"
                stroke="#2D1B0E"
                strokeWidth="14"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.4"
                transform="translate(0.8, 1.5)"
              />
              {/* Path shadow */}
              <path
                d={generatePathD()}
                fill="none"
                stroke="#5D4037"
                strokeWidth="12"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.6"
                transform="translate(0.4, 0.8)"
              />
              {/* Main path base (dark) */}
              <path
                d={generatePathD()}
                fill="none"
                stroke="#6D4C2B"
                strokeWidth="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Main path surface (lighter) */}
              <path
                d={generatePathD()}
                fill="none"
                stroke="#A67C52"
                strokeWidth="7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Path highlight (top edge) */}
              <path
                d={generatePathD()}
                fill="none"
                stroke="#D4B896"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.7"
              />
              {/* Animated golden shimmer line */}
              <path
                d={generatePathD()}
                fill="none"
                stroke="url(#pathShimmerGradient)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="path-shimmer"
                style={{ strokeDasharray: '10 20' }}
              />
              {/* Gradient definition for shimmer */}
              <defs>
                <linearGradient id="pathShimmerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(255,215,0,0.2)" />
                  <stop offset="50%" stopColor="rgba(255,215,0,0.9)" />
                  <stop offset="100%" stopColor="rgba(255,215,0,0.2)" />
                </linearGradient>
              </defs>
            </svg>

            {/* Level tiles on the path */}
            {currentLevels.map((lvl, index) => {
              const isUnlocked = lvl.id === 1 || completedLevels.includes(lvl.id - 1);
              const isCompleted = completedLevels.includes(lvl.id);
              const bestScore = getLevelBestScore(lvl.id);
              const stars = getLevelStars(lvl.id, bestScore);
              const position = getPathPosition(index, currentLevels.length);
              const isCurrent = isUnlocked && !isCompleted && lvl.id === (Math.max(...completedLevels, 0) + 1);
              
              return (
                <LevelTile
                  key={lvl.id}
                  level={lvl}
                  isUnlocked={isUnlocked}
                  isCompleted={isCompleted}
                  canPlay={canPlay}
                  stars={stars}
                  onClick={() => handleTileClick(lvl)}
                  isSelected={selectedLevel?.id === lvl.id}
                  position={position}
                  isCurrent={isCurrent}
                  index={index}
                />
              );
            })}

            {/* Page indicator */}
            <div className="absolute bottom-3 right-3 bg-black/50 text-white px-2 py-1 rounded-lg text-xs font-display">
              Levels {currentPage * LEVELS_PER_PAGE + 1}-{Math.min((currentPage + 1) * LEVELS_PER_PAGE, LEVELS.length)}
            </div>
          </div>

          {/* Page down button */}
          <Button
            onClick={() => goToPage(-1)}
            disabled={currentPage <= 0}
            size="sm"
            data-testid="page-down-btn"
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-20 bg-white/90 hover:bg-white rounded-full shadow-lg btn-hover-effect"
          >
            <ChevronDown className="w-5 h-5" />
          </Button>
        </div>
      </div>
      </div>

      {/* Level detail popup */}
      {selectedLevel && (
        <LevelDetailPopup
          level={selectedLevel}
          isCompleted={completedLevels.includes(selectedLevel.id)}
          stars={getLevelStars(selectedLevel.id, getLevelBestScore(selectedLevel.id))}
          bestScore={getLevelBestScore(selectedLevel.id)}
          canPlay={canPlay}
          onPlay={() => handlePlayLevel(selectedLevel.id)}
          onClose={() => setSelectedLevel(null)}
        />
      )}

      {/* Shop Modal */}
      <ShopModal
        isOpen={showShop}
        onClose={() => setShowShop(false)}
        totalStars={totalStars}
        totalCoins={totalCoins}
        inventory={inventory}
        equipment={equipment}
        permanentUpgrades={permanentUpgrades}
        onBuyConsumable={buyConsumable}
        onBuyPermanentUpgrade={buyPermanentUpgrade}
        onAddStars={addStars}
        onCoinsChange={(delta) => {
          // Refresh shop data to get updated coins
          refreshShopData();
        }}
        initialTab={shopInitialTab}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
      
      {/* Daily Box Reward Modal */}
      <DailyBoxReward
        isOpen={showWheel}
        onClose={closeWheel}
        onReward={handleWheelReward}
        lastSpinDate={lastSpinDate}
      />
      
      {/* Battle Pass Modal */}
      <BattlePassModal
        isOpen={showBattlePass}
        onClose={() => setShowBattlePass(false)}
        battlePass={battlePassData}
        onClaimReward={handleClaimBattlePassReward}
      />
      
      {/* Cosmetics Modal */}
      <CosmeticsModal
        isOpen={showCosmetics}
        onClose={() => setShowCosmetics(false)}
        unlockedSkins={unlockedSkins}
        unlockedEffects={unlockedEffects}
        unlockedThemes={unlockedThemes}
        unlockedFrames={unlockedFrames}
        equippedSkin={equippedSkin}
        equippedEffect={equippedEffect}
        equippedTheme={equippedTheme}
        equippedFrame={equippedFrame}
        onEquip={handleEquipCosmetic}
      />
      
      {/* Friends Modal */}
      <FriendsModal
        isOpen={showFriends}
        onClose={() => setShowFriends(false)}
      />
      
      {/* Chat Modal */}
      <ChatModal
        isOpen={showChat}
        onClose={() => setShowChat(false)}
      />
      
      {/* Inventory Modal */}
      <InventoryModal
        isOpen={showInventory}
        onClose={() => setShowInventory(false)}
        inventory={inventory}
        equipment={equipment}
        equippedItems={equippedItems}
        collectedFruits={collectedFruits}
        cosmetics={{
          unlocked_skins: unlockedSkins,
          unlocked_effects: unlockedEffects,
          unlocked_themes: unlockedThemes,
          unlocked_frames: unlockedFrames
        }}
        isPremium={battlePassData.is_premium}
        hp={hp}
        maxHP={maxHP}
        playerLevel={currentLevel}
        playerName={user?.playerName || 'Player'}
        equippedSword={equippedSword}
        onEquipSword={handleEquipSword}
        onUseItem={async (itemId) => {
          // Handle using health potions from inventory
          if (itemId === 'health_potion') {
            const consumed = await consumeItem(itemId);
            if (consumed) {
              healHP(40);
              await refreshShopData();
              return true;
            }
          } else if (itemId === 'super_health_potion') {
            const consumed = await consumeItem(itemId);
            if (consumed) {
              healHP(80);
              await refreshShopData();
              return true;
            }
          }
          return false;
        }}
        onEquip={async (itemId) => {
          const result = await equipEquipment(itemId);
          if (result) await refreshShopData();
          return result;
        }}
        onUnequip={async (itemId) => {
          const result = await unequipEquipment(itemId);
          if (result) await refreshShopData();
          return result;
        }}
        onRecharge={async (itemId) => {
          return await rechargeEquipment(itemId);
        }}
      />
      
      {/* Grand Exchange Modal */}
      <GrandExchangeModal
        isOpen={showExchange}
        onClose={() => setShowExchange(false)}
        userCoins={totalCoins}
        onCoinsChange={() => refreshShopData()}
        userInventory={inventory}
        userEquipment={equipment}
        userCollectedFruits={collectedFruits}
        userCosmetics={{
          unlocked_skins: unlockedSkins,
          unlocked_effects: unlockedEffects,
          unlocked_themes: unlockedThemes,
          unlocked_frames: unlockedFrames
        }}
        onPurchase={() => refreshShopData()}
      />
      
      {/* World Selector Modal */}
      {showWorldSelector && (
        <WorldSelector
          completedLevels={completedLevels}
          currentLevel={currentLevel}
          onSelectWorld={handleWorldSelect}
          onClose={() => setShowWorldSelector(false)}
          isPremiumMember={membershipStatus?.is_premium_member || false}
        />
      )}
      
      {/* Premium Membership Modal */}
      <PremiumMembershipModal
        isOpen={showMembership}
        onClose={() => setShowMembership(false)}
        membershipStatus={membershipStatus}
        onMembershipUpdate={() => {
          fetchMembershipStatus();
          refreshShopData();
        }}
      />

      {/* PvP Arena Modal */}
      <PvPArenaModal
        isOpen={showPvP}
        onClose={() => setShowPvP(false)}
        userCoins={totalCoins}
        onCoinsChange={() => refreshShopData()}
        currentLevel={currentLevel}
      />

      {/* Profile Switcher Modal */}
      <ProfileSwitcherModal
        isOpen={showProfileSwitcher}
        onClose={() => setShowProfileSwitcher(false)}
      />

      {/* Daily Reward Modal */}
      <DailyRewardModal
        isOpen={showDailyReward}
        onClose={() => setShowDailyReward(false)}
        onClaimed={() => refreshShopData()}
      />
    </>
  );
}
